import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import {
  DataApiResponse,
  DiscountBlockModel,
  DiscountImportModel,
  DiscountImportResultModel,
  DiscountModel,
} from '@digilize/shared/definitions/src';

@Injectable({ providedIn: 'root' })
export class DiscountsApiService {
  readonly DISCOUNTS_URL = '/api/v1.0/discounts';

  constructor(private http: HttpClient) {}

  getDiscounts(params: Record<string, any>) {
    return this.http.get<DataApiResponse<DiscountModel[]>>(this.DISCOUNTS_URL, { params });
  }

  getDiscount(id: string) {
    return this.http.get<DiscountModel>(`${this.DISCOUNTS_URL}/${id}`);
  }

  addDiscount(data: DiscountModel) {
    return this.http.post<DiscountModel>(this.DISCOUNTS_URL, data);
  }

  editDiscount(data: DiscountModel) {
    return this.http.put<DiscountModel>(this.DISCOUNTS_URL, data);
  }

  copyDiscount(id: string) {
    return this.http.post<DiscountModel>(`${this.DISCOUNTS_URL}/${id}/copy`, {});
  }

  deleteDiscount(id: string) {
    return this.http.delete(`${this.DISCOUNTS_URL}/${id}`);
  }

  syncGeneralInfo(data: Record<string, any>) {
    return this.http.put<DiscountModel>(`${this.DISCOUNTS_URL}/general-info`, data);
  }

  syncPos(data: Record<string, any>) {
    return this.http.put<DiscountModel>(`${this.DISCOUNTS_URL}/pos`, data);
  }

  syncConfiguration(data: Record<string, any>) {
    return this.http.put<DiscountModel>(`${this.DISCOUNTS_URL}/configuration`, data);
  }

  syncValidity(data: Record<string, any>) {
    return this.http.put<DiscountModel>(`${this.DISCOUNTS_URL}/validity`, data);
  }

  syncProducts(data: Record<string, any>) {
    return this.http.put<DiscountModel>(`${this.DISCOUNTS_URL}/products`, data);
  }

  syncRules(data: Record<string, any>) {
    return this.http.put<DiscountModel>(`${this.DISCOUNTS_URL}/rules`, data);
  }

  syncCodeLimit(data: Record<string, any>) {
    return this.http.put<DiscountModel>(`${this.DISCOUNTS_URL}/code-limit`, data);
  }

  syncBoxOffice(data: Record<string, any>) {
    return this.http.put<DiscountModel>(`${this.DISCOUNTS_URL}/box-office`, data);
  }

  updateStatus(id: string, status: 'draft' | 'active') {
    return this.http.put<DiscountModel>(`/api/v2.0/discounts/status`, { id, entity_status: status });
  }

  getDiscountImports(discountId: string) {
    return this.http.get<DataApiResponse<DiscountImportModel[]>>(`${this.DISCOUNTS_URL}/imports`, {
      params: { discount: discountId },
    });
  }

  createDiscountImport(discountId: string, data: FormData) {
    return this.http.post<DiscountImportResultModel>(`${this.DISCOUNTS_URL}/${discountId}/imports`, data);
  }

  exportDiscountImport(importId: string): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.DISCOUNTS_URL}/imports/${importId}/export`, {
      responseType: 'blob',
      observe: 'response',
    }) as Observable<HttpResponse<Blob>>;
  }

  deleteDiscountImport(importId: string) {
    return this.http.delete(`${this.DISCOUNTS_URL}/imports/${importId}`);
  }

  getDiscountBlocks(discountId: string, params?: { from?: string; to?: string }) {
    return this.http.get<DiscountBlockModel[]>(`${this.DISCOUNTS_URL}/${discountId}/blocks`, {
      params: params as Record<string, string> | undefined,
    });
  }

  addDiscountBlock(discountId: string, data: DiscountBlockModel) {
    return this.http.post<DiscountBlockModel>(`${this.DISCOUNTS_URL}/${discountId}/blocks`, data);
  }

  editDiscountBlock(discountId: string, blockId: string, data: DiscountBlockModel) {
    return this.http.put<DiscountBlockModel>(`${this.DISCOUNTS_URL}/${discountId}/blocks/${blockId}`, data);
  }

  deleteDiscountBlock(discountId: string, blockId: string) {
    return this.http.delete(`${this.DISCOUNTS_URL}/${discountId}/blocks/${blockId}`);
  }
}
